import {
  ArrowLeftOutlined,
  BellOutlined,
  CaretDownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SearchOutlined,
  KeyOutlined,
  LaptopOutlined,
  PoweroffOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { App, Avatar, Badge, Button, Dropdown, Flex, Grid, Layout, Menu, Space, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ContextSwitcher } from '@/components/organization/ContextSwitcher';
import { TopNavigation } from '@/components/navigation/TopNavigation';
import { WorkspaceTabs } from '@/components/navigation/WorkspaceTabs';
import { queryClient } from '@/api/query-client';
import { useSessionStore } from '@/stores/session-store';
import { getAvailableNavigationItems, getNavigationGroup, navigationGroups } from '@/config/navigation';
import { useNavigationStore } from '@/stores/navigation-store';
import { preloadRouteModule } from '@/app/route-modules';
import { getIdlePreloadPaths, scheduleIdleRoutePreloads, shouldPreloadRoutes } from '@/app/idle-route-preloader';
import { useOrganizationContextBroadcastSync, useOrganizationContextSwitch } from '@/components/organization/useOrganizationContextSwitch';
import { api } from '@/api/services';
import { getUserFacingErrorMessage } from '@/components/feedback/errorMessages';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const ChangePasswordModal = lazy(() => import('@/components/account/ChangePasswordModal').then((module) => ({ default: module.ChangePasswordModal })));
const SessionsModal = lazy(() => import('@/components/account/SessionsModal').then((module) => ({ default: module.SessionsModal })));

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const { message, modal } = App.useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const me = useSessionStore((state) => state.me);
  const openTab = useNavigationStore((state) => state.openTab);
  const closeAll = useNavigationStore((state) => state.closeAll);
  const clearSession = useSessionStore((state) => state.clearSession);
  const pageRefreshToken = useNavigationStore((state) => state.refreshTokens[location.pathname] ?? 0);
  const [collapsed, setCollapsed] = useState(() => window.innerWidth < 992);
  const [openMenuGroups, setOpenMenuGroups] = useState<string[]>(() => navigationGroups.map((group) => `group:${group.key}`));
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuCloseTimer = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const contextSwitch = useOrganizationContextSwitch();
  const isMobile = screens.lg === false;

  useOrganizationContextBroadcastSync();

  const navigateTo = (path: string) => {
    if (path === location.pathname) return;
    navigate(path);
  };

  useEffect(() => {
    if (screens.lg === false) setCollapsed(true);
  }, [screens.lg]);

  useEffect(() => {
    if (me?.user.mustChangePassword) setPasswordOpen(true);
  }, [me?.user.mustChangePassword]);

  const availableNavigationItems = useMemo(() => getAvailableNavigationItems(me), [me]);

  useEffect(() => {
    const connection = (navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }).connection;
    if (!shouldPreloadRoutes(connection)) return undefined;

    const paths = getIdlePreloadPaths(
      availableNavigationItems.map((item) => item.path),
      location.pathname,
    );
    return scheduleIdleRoutePreloads(paths, preloadRouteModule);
  }, [availableNavigationItems, location.pathname]);

  const menuItems = useMemo<MenuProps['items']>(() =>
    navigationGroups.flatMap((group) => {
      const children = availableNavigationItems
        .filter((item) => item.group === group.key)
        .map((item) => ({
          key: item.path,
          label: <span onPointerEnter={() => { void preloadRouteModule(item.path); }} onFocus={() => { void preloadRouteModule(item.path); }}>{t(item.titleKey)}</span>,
        }));
      return children.length > 0 ? [{
        key: `group:${group.key}`,
        icon: group.icon,
        label: t(group.titleKey),
        children,
      }] : [];
    }), [availableNavigationItems, t]);

  useEffect(() => {
    const activeGroupKey = `group:${getNavigationGroup(location.pathname)}`;
    setOpenMenuGroups((current) => current.includes(activeGroupKey) ? current : [...current, activeGroupKey]);
  }, [location.pathname]);

  const localeMenu: MenuProps = {
    items: [{ key: 'zh-TW', label: '繁體中文' }, { key: 'en', label: 'English' }],
    selectedKeys: [i18n.language],
    onClick: ({ key }) => {
      void i18n.changeLanguage(key);
      localStorage.setItem('invoice-locale', key);
    },
  };

  const openAccountMenu = () => {
    if (accountMenuCloseTimer.current) globalThis.clearTimeout(accountMenuCloseTimer.current);
    accountMenuCloseTimer.current = null;
    setAccountMenuOpen(true);
  };
  const scheduleAccountMenuClose = () => {
    if (accountMenuCloseTimer.current) globalThis.clearTimeout(accountMenuCloseTimer.current);
    accountMenuCloseTimer.current = globalThis.setTimeout(() => setAccountMenuOpen(false), 220);
  };

  useEffect(() => () => {
    if (accountMenuCloseTimer.current) globalThis.clearTimeout(accountMenuCloseTimer.current);
  }, []);

  useEffect(() => {
    if (!accountMenuOpen) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.profile-button, .account-dropdown')) return;
      setAccountMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer, true);
    return () => document.removeEventListener('pointerdown', closeOnOutsidePointer, true);
  }, [accountMenuOpen]);

  const parentOrganization = me?.context.organization.path.at(-2);
  const accountMenu: MenuProps = {
    items: [
      { key: 'password', icon: <KeyOutlined />, label: t('common.changePassword') },
      { key: 'sessions', icon: <LaptopOutlined />, label: t('account.sessionsTitle') },
      { key: 'parent', icon: <ArrowLeftOutlined />, label: t('common.returnToParent'), disabled: !parentOrganization },
      { type: 'divider' },
      { key: 'logout', icon: <PoweroffOutlined />, label: t('common.logout'), danger: true },
    ],
    onClick: ({ key }) => {
      setAccountMenuOpen(false);
      if (key === 'password') setPasswordOpen(true);
      if (key === 'sessions') setSessionsOpen(true);
      if (key === 'parent' && parentOrganization) {
        void contextSwitch.mutateAsync({ organizationId: parentOrganization.id }).then(() => {
          openTab('/dashboard');
          navigate('/dashboard');
          message.success(t('context.switched'));
        }).catch((error) => message.error(getUserFacingErrorMessage(error)));
      }
      if (key === 'logout') modal.confirm({
        title: t('account.logoutTitle'), content: t('account.logoutDescription'), okText: t('common.logout'), cancelText: t('common.cancel'), okButtonProps: { danger: true },
        onOk: async () => {
          await api.logout();
          await queryClient.cancelQueries();
          queryClient.clear();
          closeAll();
          clearSession('anonymous');
          window.history.replaceState(window.history.state, '', '/login');
          window.dispatchEvent(new PopStateEvent('popstate'));
        },
      });
    },
  };

  return (
    <Layout className="app-shell">
      <a href="#main-content" className="skip-link">跳至主要內容</a>
      <Sider
        collapsible
        collapsed={collapsed}
        trigger={null}
        width={248}
        collapsedWidth={isMobile ? 0 : 72}
        className="app-sider"
      >
        <button type="button" className="brand" onClick={() => navigateTo('/dashboard')} aria-label={t('app.name')}>
          <span className="brand-mark"><img src="/images/taiwan-einvoice-mark-v2-reversed.svg" alt="" /></span>
          {!collapsed && <span><strong>{t('app.name')}</strong><small>{t('app.subtitle')}</small></span>}
        </button>
        <Menu
          mode="inline"
          inlineIndent={14}
          selectedKeys={[location.pathname]}
          openKeys={collapsed ? [] : openMenuGroups}
          onOpenChange={(keys) => {
            if (!collapsed) setOpenMenuGroups(keys.map(String));
          }}
          items={menuItems}
          onClick={({ key }) => {
            openTab(key);
            navigateTo(key);
            if (isMobile) setCollapsed(true);
          }}
          className="main-menu"
        />
        {!collapsed && (
          <div className="sider-health">
            <span className="health-dot" />
            <div><strong>服務狀態正常</strong><small>最後更新 14:32</small></div>
          </div>
        )}
      </Sider>
      <Layout>
        <Header className="app-header">
          <Flex align="center" gap={10} className="header-start">
            <Button type="text" aria-label="切換導覽列" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed((value) => !value)} />
            <ContextSwitcher />
          </Flex>
          <div className="header-center">
            <TopNavigation />
          </div>
          <Flex align="center" gap={4} className="header-actions">
            <Tooltip title={t('navigation.globalSearch')}>
              <Button type="text" shape="circle" icon={<SearchOutlined />} aria-label={t('navigation.globalSearch')} />
            </Tooltip>
            <Dropdown menu={localeMenu} trigger={['click']}>
              <Button type="text">{i18n.language === 'zh-TW' ? '繁中' : 'EN'} <CaretDownOutlined /></Button>
            </Dropdown>
            <Tooltip title="通知">
              <Badge count={3} size="small"><Button type="text" shape="circle" icon={<BellOutlined />} aria-label="通知" /></Badge>
            </Tooltip>
            <div
              className={`account-menu-wrapper${accountMenuOpen ? ' is-open' : ''}`}
              onMouseLeave={scheduleAccountMenuClose}
            >
              <Button
                type="text"
                className="profile-button"
                aria-expanded={accountMenuOpen}
                aria-haspopup="menu"
                onFocus={openAccountMenu}
                onBlur={scheduleAccountMenuClose}
                onClick={openAccountMenu}
              >
                <Space><Avatar size={30} icon={<UserOutlined />} /><span className="desktop-only">{me?.user.displayName ?? '--'}</span><CaretDownOutlined /></Space>
              </Button>
              <div className="account-dropdown">
                <Menu selectable={false} items={accountMenu.items} onClick={accountMenu.onClick} />
              </div>
            </div>
          </Flex>
        </Header>
        <WorkspaceTabs />
        <Content id="main-content" className="app-content" tabIndex={-1}>
          <Suspense
            fallback={(
              <div className="route-loading" role="status" aria-live="polite">
                <div className="bootstrap-line" />
                <span className="sr-only">正在載入頁面</span>
              </div>
            )}
          >
            <div className="route-view" key={`${location.pathname}:${pageRefreshToken}`}>
              <Outlet />
            </div>
          </Suspense>
        </Content>
      </Layout>
      {isMobile && !collapsed && <button className="mobile-scrim" aria-label="關閉導覽列" onClick={() => setCollapsed(true)} />}
      {passwordOpen && <Suspense fallback={null}><ChangePasswordModal open forced={me?.user.mustChangePassword} onCancel={() => setPasswordOpen(false)} /></Suspense>}
      {sessionsOpen && <Suspense fallback={null}><SessionsModal open onCancel={() => setSessionsOpen(false)} /></Suspense>}
    </Layout>
  );
}
